/**
 * Workflow run notifier
 *
 * One app-wide owner for "is that extraction done yet?".
 *
 * The watch used to live in `useKpiWorkflows`, which is mounted by `/templates`
 * — so the poller died with the page. A run takes minutes, the user leaves, and
 * the completion is invisible until they happen to come back. Like the upload
 * manager, this provider sits ABOVE the inner router so the watch outlives the
 * page that started it and the outcome can be announced on whatever page the
 * user is on.
 *
 * It is also the ONLY poller for a tracked run: the Templates page reads runs
 * back out of here rather than running a second loop over the same endpoint.
 *
 * Two invariants earn most of the code here:
 *
 *   1. A run this app cannot prove has finished stays ACTIVE. Reloading the tab
 *      re-adopts whatever the backend says is queued or running, and a watch
 *      that runs out of attempts — or that cannot read the status at all —
 *      drops to a slower `unknown` phase instead of being retired as if it had
 *      settled. "Run" staying disabled while a run may still be live is a
 *      wasted click; re-enabling it early is a duplicate extraction bill.
 *   2. Nothing a superseded watch does can be seen. Clearing a timeout does not
 *      stop a callback that is already awaiting `listRuns`, so every watch
 *      carries a generation that is invalidated on untrack/replace/unmount and
 *      re-checked after each await, before any state change, announcement or
 *      reschedule.
 */

import React, {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import type { WorkflowToastMessage } from "../components/Workflows/WorkflowToast";
import {
	listRuns,
	listWorkflows,
	WorkflowNotFoundError,
} from "../services/kpiWorkflowService";
import { maybeNotifyDesktop } from "../utils/desktopNotification";
import {
	isTerminalRunStatus,
	type Workflow,
	type WorkflowRun,
} from "../types/kpiWorkflow";

export const POLL_INTERVAL_MS = 5000;
/** ~10 minutes of close watching before the run is treated as unknown. */
export const MAX_POLL_ATTEMPTS = 120;
/**
 * How often an `unknown` run is re-checked. Slower than the active watch on
 * purpose: at this point the answer is overdue rather than imminent, and the
 * only thing still riding on it is re-enabling the workflow's Run button.
 */
export const RECONCILE_INTERVAL_MS = 30000;
/**
 * How many settled announcements can be on screen at once. Older ones are
 * dropped rather than the newest suppressed — the most recent outcome is the
 * one the user is most likely still waiting on.
 */
export const MAX_NOTIFICATIONS = 4;
/**
 * How long to wait before trying the "what is already running?" sweep again.
 *
 * There IS a retry, and until one attempt succeeds the app holds itself as
 * not-yet-knowing: a failed sweep that quietly declared itself done would
 * re-enable Run over a live extraction, which is the expensive mistake.
 */
export const HYDRATION_RETRY_MS = 10000;
/**
 * Settlement events kept for consumers to drain. Far more than a React batch
 * can ever produce at once — the queue exists so that two runs settling in the
 * same tick both get their refetch, not as a general-purpose log.
 */
export const MAX_SETTLEMENTS = 50;

/**
 * A message plus its own id, so a consumer can dismiss one announcement out of
 * several without touching the rest.
 */
export interface WorkflowRunNotification extends WorkflowToastMessage {
	id: number;
	/**
	 * Which workflow this is about, so the toast's "View run history" can open
	 * THAT dashboard (DOP-478). Before the unified IA every workflow shared one
	 * destination and the link did not need to know.
	 */
	workflowId: string;
}

/**
 * `watching`: polling closely, the run is expected to settle soon.
 * `unknown`: the app has not been able to confirm an outcome — the run is
 * treated as possibly still live and re-checked slowly until it settles.
 */
export type TrackedRunPhase = "watching" | "unknown";

export interface TrackedRun {
	workflowId: string;
	runId: string;
	phase: TrackedRunPhase;
}

/** One run reaching a terminal state — a refresh cue for consumers. */
export interface SettledRun {
	workflowId: string;
	runId: string;
	/** Monotonic across the provider's life; consumers drain by cursor. */
	count: number;
}

export interface WorkflowRunNotifierValue {
	/** Start watching a run that has already been queued on the backend. */
	trackRun: (workflowId: string, runId: string) => void;
	/** Stop watching — the caller already knows the run settled. */
	untrackRun: (workflowId: string, runId: string) => void;
	/**
	 * Drop everything held about a workflow that no longer exists (DOP-491):
	 * its watch, whatever run it was on, and its cached history. Unlike
	 * `untrackRun` it needs no run id — the caller deleted the whole workflow
	 * and should not have to know which run, if any, was still being watched.
	 */
	forgetWorkflow: (workflowId: string) => void;
	/**
	 * Adopt from a RUN HISTORY read (DOP-276).
	 *
	 * `adoptWorkflows` covers the workflow list, whose `last_run` is one run per
	 * workflow. A run history is the other place a live run becomes visible —
	 * expanding a card, or a page that loads a history directly — and a row
	 * discovered there used to sit frozen because nothing was watching it.
	 */
	adoptRuns: (workflowId: string, runs: WorkflowRun[]) => void;
	/**
	 * Poll every tracked run once, now.
	 *
	 * The provider's own return-to-the-tab reconcile calls this; it is exposed
	 * so a consumer that knows the data just moved can ask for the same thing
	 * instead of starting a loop of its own.
	 */
	refreshTrackedRuns: () => void;
	/** Runs being watched or reconciled, keyed by workflow. */
	trackedRuns: Record<string, TrackedRun>;
	/** Workflows with a run that is not known to have finished. */
	trackedWorkflowIds: Set<string>;
	/** Latest run list seen by the watch, per workflow it polled. */
	runsByWorkflow: Record<string, WorkflowRun[]>;
	/**
	 * Every run that has settled, oldest first. Page-level state that mirrors
	 * the workflow list or a run history drains this by cursor rather than
	 * polling on its own.
	 *
	 * A queue and not a "most recent": two runs can settle inside one React
	 * batch, and a singleton would leave the earlier workflow's card and history
	 * showing a run that finished.
	 */
	settlements: SettledRun[];
	/**
	 * Adopt whatever is already in flight, from a workflow list the CALLER
	 * fetched. Any successful read of the list counts as a hydration source, so
	 * the page's own load rescues a sweep that failed here.
	 */
	adoptWorkflows: (workflows: Workflow[]) => void;
	/**
	 * True until some source has successfully reported what is already running.
	 * Consumers must treat it as "do not offer to start a run yet" — this is the
	 * fail-closed half of adoption.
	 */
	isHydrating: boolean;
	notifications: WorkflowRunNotification[];
	dismissNotification: (id: number) => void;
}

const WorkflowRunNotifierContext = createContext<
	WorkflowRunNotifierValue | undefined
>(undefined);

export const useWorkflowRunNotifier = (): WorkflowRunNotifierValue => {
	const context = useContext(WorkflowRunNotifierContext);
	if (!context) {
		throw new Error(
			"useWorkflowRunNotifier must be used within a WorkflowRunNotifierProvider",
		);
	}
	return context;
};

/** The sentence a settled run gets announced with. */
export function describeCompletion(run: WorkflowRun): WorkflowToastMessage {
	if (run.status === "cancelled") {
		// Not an error — the user asked for this. And not a partial update
		// either: a cancelled run's writes are rolled back, so the dashboard is
		// exactly as it was before the run started.
		return {
			text: `Extraction cancelled after ${run.files_processed} of ${run.files_total} files. Nothing was changed — the readings are kept in the run's history.`,
			variant: "info",
		};
	}
	if (run.status === "failed") {
		return {
			text: run.error
				? `Extraction failed: ${run.error}`
				: "Extraction failed. Check the run history for details.",
			variant: "error",
		};
	}
	const skipped = run.skipped.length;
	const base = `KPI extraction complete — ${run.files_processed} of ${run.files_total} files processed`;
	return skipped > 0
		? {
				text: `${base}, ${skipped} skipped. Open the run history to see why.`,
				variant: "warning",
			}
		: { text: `${base}.`, variant: "success" };
}

/** One live watch. `generation` is what makes a superseded callback a no-op. */
interface Watch {
	runId: string;
	generation: number;
	phase: TrackedRunPhase;
	timer: ReturnType<typeof setTimeout> | null;
	/**
	 * Where this watch is in its attempt budget. Carried on the watch rather
	 * than only in the poll closure so an out-of-band refresh (returning to the
	 * tab) can resume the loop where it was instead of restarting the budget.
	 */
	attempt: number;
	/**
	 * A request for this watch is out. `focus` and `visibilitychange` both fire
	 * on the same tab switch, and without this the pair would put two identical
	 * reads on the wire for every return to the page.
	 */
	inFlight: boolean;
}

interface WorkflowRunNotifierProviderProps {
	children: ReactNode;
}

export const WorkflowRunNotifierProvider: React.FC<
	WorkflowRunNotifierProviderProps
> = ({ children }) => {
	const [trackedRuns, setTrackedRuns] = useState<Record<string, TrackedRun>>(
		{},
	);
	const [runsByWorkflow, setRunsByWorkflow] = useState<
		Record<string, WorkflowRun[]>
	>({});
	const [settlements, setSettlements] = useState<SettledRun[]>([]);
	const [notifications, setNotifications] = useState<WorkflowRunNotification[]>(
		[],
	);
	const [isHydrating, setIsHydrating] = useState(true);

	/** One watch per workflow: a workflow does not run twice at once. */
	const watches = useRef<Map<string, Watch>>(new Map());
	const nextGeneration = useRef(0);
	const settledCount = useRef(0);
	const nextNotificationId = useRef(0);
	const isMounted = useRef(true);

	useEffect(() => {
		isMounted.current = true;
		const live = watches.current;
		return () => {
			isMounted.current = false;
			for (const watch of live.values()) {
				// Invalidate as well as clear: a callback already past its timeout
				// and awaiting a response has to find itself superseded.
				watch.generation = -1;
				if (watch.timer) clearTimeout(watch.timer);
			}
			live.clear();
		};
	}, []);

	/**
	 * `isTerminal` says whether this is news about the RUN (done/failed/
	 * cancelled) or about the app's ability to watch it. Only the former is
	 * worth an OS notification; the in-app toast is identical either way.
	 */
	const announce = useCallback(
		(
			workflowId: string,
			message: WorkflowToastMessage,
			isTerminal = false,
		) => {
			nextNotificationId.current += 1;
			const entry = { ...message, id: nextNotificationId.current, workflowId };
			setNotifications((current) =>
				[...current, entry].slice(-MAX_NOTIFICATIONS),
			);
			// Additive: when the tab is visible the toast above is the whole
			// announcement, exactly as before.
			maybeNotifyDesktop({
				text: message.text,
				variant: message.variant,
				isTerminal,
			});
		},
		[],
	);

	const dismissNotification = useCallback((id: number) => {
		setNotifications((current) => current.filter((entry) => entry.id !== id));
	}, []);

	const setPhase = useCallback(
		(workflowId: string, runId: string, phase: TrackedRunPhase) => {
			setTrackedRuns((current) => {
				const existing = current[workflowId];
				if (existing?.runId === runId && existing.phase === phase) {
					return current;
				}
				return { ...current, [workflowId]: { workflowId, runId, phase } };
			});
		},
		[],
	);

	const clearPhase = useCallback((workflowId: string, runId: string) => {
		setTrackedRuns((current) => {
			// A watch that was already replaced by a newer run must not delete the
			// newer one's entry on its way out.
			if (current[workflowId]?.runId !== runId) return current;
			const next = { ...current };
			delete next[workflowId];
			return next;
		});
	}, []);

	/** Retire a watch: no timer, no valid generation, no tracked state. */
	const stopWatch = useCallback(
		(workflowId: string, runId: string) => {
			const watch = watches.current.get(workflowId);
			if (watch && watch.runId === runId) {
				watch.generation = -1;
				if (watch.timer) clearTimeout(watch.timer);
				watches.current.delete(workflowId);
			}
			clearPhase(workflowId, runId);
		},
		[clearPhase],
	);

	const untrackRun = useCallback(
		(workflowId: string, runId: string) => stopWatch(workflowId, runId),
		[stopWatch],
	);

	/**
	 * Forget a workflow outright — it was deleted.
	 *
	 * Not `stopWatch`: that retires one run by id and only if it is still the
	 * current one, which is right for a run that settled and wrong here, where
	 * nothing about the workflow is worth keeping. A watch left behind would
	 * poll a 404 every 30 seconds until the tab closed, since the slow lane has
	 * no ceiling on purpose.
	 */
	const forgetWorkflow = useCallback((workflowId: string) => {
		const watch = watches.current.get(workflowId);
		if (watch) {
			watch.generation = -1;
			if (watch.timer) clearTimeout(watch.timer);
			watches.current.delete(workflowId);
		}
		const without = <T,>(current: Record<string, T>) => {
			if (!(workflowId in current)) return current;
			const next = { ...current };
			delete next[workflowId];
			return next;
		};
		setTrackedRuns(without);
		setRunsByWorkflow(without);
	}, []);

	/**
	 * The watch loop.
	 *
	 * `generation` is captured per scheduling and re-checked after every await:
	 * an untrack, a replacement run, or an unmount that happened while the
	 * response was in flight makes everything below the check a no-op.
	 */
	const poll = useCallback(
		(
			workflowId: string,
			runId: string,
			generation: number,
			attempt: number,
			phase: TrackedRunPhase,
		) => {
			const isCurrent = () =>
				isMounted.current &&
				watches.current.get(workflowId)?.generation === generation;

			const schedule = (
				nextAttempt: number,
				nextPhase: TrackedRunPhase,
				delay: number,
			) => {
				if (!isCurrent()) return;
				const watch = watches.current.get(workflowId);
				if (!watch) return;
				watch.phase = nextPhase;
				watch.attempt = nextAttempt;
				watch.timer = setTimeout(
					() => poll(workflowId, runId, generation, nextAttempt, nextPhase),
					delay,
				);
			};

			/**
			 * Out of close-watching attempts. The run is NOT retired: it moves to
			 * the slow lane, still counted as active, and says so — going quiet
			 * here would read as "it vanished", and dropping it would re-enable a
			 * Run button over a run that may still be burning tokens.
			 */
			const giveUpWatching = (couldNotRead: boolean) => {
				if (!isCurrent()) return;
				setPhase(workflowId, runId, "unknown");
				announce(
					workflowId,
					couldNotRead
						? {
								text: "Could not check on this extraction. It may still be running — check its workflow for the status.",
								variant: "warning",
							}
						: {
								text: "This extraction is still running. Check its workflow for the status.",
								variant: "info",
							},
				);
				schedule(0, "unknown", RECONCILE_INTERVAL_MS);
			};

			const atCeiling =
				phase === "watching" && attempt + 1 >= MAX_POLL_ATTEMPTS;

			// Latched before the request goes out, so a second call in the same
			// tick (focus and visibilitychange both fire on a tab switch) finds
			// this watch busy and does not duplicate the read.
			const latch = watches.current.get(workflowId);
			if (latch && latch.generation === generation) latch.inFlight = true;

			const release = () => {
				const watch = watches.current.get(workflowId);
				if (watch && watch.generation === generation) watch.inFlight = false;
			};

			void (async () => {
				if (!isCurrent()) return;
				try {
					const runs = await listRuns(workflowId);
					if (!isCurrent()) return;
					setRunsByWorkflow((current) => ({ ...current, [workflowId]: runs }));
					release();
					const run = runs.find((candidate) => candidate.id === runId);
					if (run && isTerminalRunStatus(run.status)) {
						stopWatch(workflowId, runId);
						announce(workflowId, describeCompletion(run), true);
						settledCount.current += 1;
						const settled: SettledRun = {
							workflowId,
							runId,
							count: settledCount.current,
						};
						// Appended, never replaced: two watches can settle inside one
						// React batch, and overwriting would leave the first workflow's
						// card and history showing a run that is already over.
						setSettlements((current) =>
							[...current, settled].slice(-MAX_SETTLEMENTS),
						);
						return;
					}
					if (atCeiling) {
						giveUpWatching(false);
						return;
					}
					schedule(
						attempt + 1,
						phase,
						phase === "watching" ? POLL_INTERVAL_MS : RECONCILE_INTERVAL_MS,
					);
				} catch (error) {
					release();
					// A transient read failure must not end the watch — but it must not
					// spend the whole attempt budget in silence either. Re-check
					// currency first: the failure may have arrived after an untrack.
					if (!isCurrent()) return;
					if (error instanceof WorkflowNotFoundError) {
						// Not transient: the workflow was deleted — here or in another
						// tab — while this watch was on it (DOP-491). Every later poll
						// would get the same 404, and the slow lane never gives up.
						forgetWorkflow(workflowId);
						return;
					}
					if (atCeiling) {
						giveUpWatching(true);
						return;
					}
					schedule(
						attempt + 1,
						phase,
						phase === "watching" ? POLL_INTERVAL_MS : RECONCILE_INTERVAL_MS,
					);
				}
			})();
		},
		[announce, forgetWorkflow, setPhase, stopWatch],
	);

	const trackRun = useCallback(
		(workflowId: string, runId: string) => {
			const existing = watches.current.get(workflowId);
			// Tracking the same run twice would double the polling and announce it
			// twice; the first watch is already the one that will report.
			if (existing?.runId === runId) return;
			// A different run for the same workflow supersedes the old watch — the
			// old one can no longer be the workflow's current state.
			if (existing) stopWatch(workflowId, existing.runId);

			nextGeneration.current += 1;
			const generation = nextGeneration.current;
			const watch: Watch = {
				runId,
				generation,
				phase: "watching",
				timer: null,
				attempt: 0,
				inFlight: false,
			};
			watches.current.set(workflowId, watch);
			setPhase(workflowId, runId, "watching");
			watch.timer = setTimeout(
				() => poll(workflowId, runId, generation, 0, "watching"),
				POLL_INTERVAL_MS,
			);
		},
		[poll, setPhase, stopWatch],
	);

	/**
	 * Adopt the live run out of a run history someone read (DOP-276).
	 *
	 * The NEWEST non-terminal row wins — one workflow can hold more than one
	 * unfinished row (a cancel that has not rolled back yet, a forced re-run)
	 * and the watch is per workflow, so it has to be the current one. That is
	 * the FIRST match in list order: the backend returns a history "most
	 * recently requested first" (`list_runs` orders by id descending), and a run
	 * carries no creation timestamp of its own to sort by.
	 *
	 * Deliberately NOT a hydration source. Hydration is the app-wide question
	 * "what is already running anywhere?", and one workflow's history cannot
	 * answer it — treating it as an answer would re-enable Run over every OTHER
	 * workflow's live extraction, which is the expensive mistake this provider
	 * exists to avoid.
	 */
	const adoptRuns = useCallback(
		(workflowId: string, runs: WorkflowRun[]) => {
			if (!isMounted.current) return;
			const live = runs.find((run) => !isTerminalRunStatus(run.status));
			if (live) trackRun(workflowId, live.id);
		},
		[trackRun],
	);

	/**
	 * Re-poll every live watch immediately, resuming its attempt budget.
	 *
	 * A backgrounded tab's timers are throttled, so returning to the page is
	 * exactly when what it shows is most stale. The per-watch in-flight latch is
	 * what makes this safe to call twice in a tick.
	 */
	const refreshTrackedRuns = useCallback(() => {
		if (!isMounted.current) return;
		for (const [workflowId, watch] of Array.from(watches.current.entries())) {
			if (watch.generation < 0 || watch.inFlight) continue;
			if (watch.timer) {
				clearTimeout(watch.timer);
				watch.timer = null;
			}
			poll(
				workflowId,
				watch.runId,
				watch.generation,
				watch.attempt,
				watch.phase,
			);
		}
	}, [poll]);

	/**
	 * Come back to runs that are current, not to whatever the tab last saw.
	 *
	 * `focus` and `visibilitychange` both fire on a tab switch; the hidden guard
	 * and the in-flight latch collapse the pair into one read per watch.
	 */
	useEffect(() => {
		const onReturn = () => {
			if (typeof document !== "undefined" && document.hidden) return;
			refreshTrackedRuns();
		};
		window.addEventListener("focus", onReturn);
		document.addEventListener("visibilitychange", onReturn);
		return () => {
			window.removeEventListener("focus", onReturn);
			document.removeEventListener("visibilitychange", onReturn);
		};
	}, [refreshTrackedRuns]);

	/**
	 * Adopt whatever is already in flight, from a workflow list someone read.
	 *
	 * Without this, a reload is amnesia: the tab forgets a live extraction, the
	 * Run button comes back, and pressing it pays for the same work twice. The
	 * workflow list carries `last_run`, so a list read is enough to find them —
	 * there is no "active runs" endpoint to ask instead.
	 *
	 * ANY successful list read counts, whoever made it. The provider's own sweep
	 * is just the first attempt: if it fails while the Templates page's load
	 * succeeds, the page's rows land here and hydration is satisfied by them.
	 */
	const adoptWorkflows = useCallback(
		(workflows: Workflow[]) => {
			if (!isMounted.current) return;
			for (const workflow of workflows) {
				const run = workflow.last_run;
				if (run && !isTerminalRunStatus(run.status)) {
					trackRun(workflow.id, run.id);
				}
			}
			setIsHydrating(false);
		},
		[trackRun],
	);

	/**
	 * The provider's own sweep — and it retries.
	 *
	 * Failing this used to be quietly fatal: the sweep marked itself done, the
	 * page's load succeeded, and Run came back over a live extraction. Now the
	 * only thing that ends hydration is a list that was actually read, from here
	 * or from a consumer, so the closed state is the failure state.
	 */
	const hydrationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isHydratingRef = useRef(true);
	isHydratingRef.current = isHydrating;
	const hasSweptOnce = useRef(false);

	const sweepForActiveRuns = useCallback(() => {
		void (async () => {
			try {
				const workflows = await listWorkflows();
				if (!isMounted.current || !isHydratingRef.current) return;
				adoptWorkflows(workflows);
			} catch {
				// Nothing to say — an unreachable API is the page's news to break.
				// But the app stays in "do not offer to run" until someone succeeds.
				if (!isMounted.current || !isHydratingRef.current) return;
				hydrationTimer.current = setTimeout(
					sweepForActiveRuns,
					HYDRATION_RETRY_MS,
				);
			}
		})();
	}, [adoptWorkflows]);

	useEffect(() => {
		if (hasSweptOnce.current) return;
		hasSweptOnce.current = true;
		sweepForActiveRuns();
		return () => {
			if (hydrationTimer.current) clearTimeout(hydrationTimer.current);
		};
	}, [sweepForActiveRuns]);

	const trackedWorkflowIds = useMemo(
		() => new Set(Object.keys(trackedRuns)),
		[trackedRuns],
	);

	const value = useMemo(
		() => ({
			trackRun,
			untrackRun,
			forgetWorkflow,
			adoptRuns,
			refreshTrackedRuns,
			trackedRuns,
			trackedWorkflowIds,
			runsByWorkflow,
			settlements,
			adoptWorkflows,
			isHydrating,
			notifications,
			dismissNotification,
		}),
		[
			trackRun,
			untrackRun,
			forgetWorkflow,
			adoptRuns,
			refreshTrackedRuns,
			trackedRuns,
			trackedWorkflowIds,
			runsByWorkflow,
			settlements,
			adoptWorkflows,
			isHydrating,
			notifications,
			dismissNotification,
		],
	);

	return (
		<WorkflowRunNotifierContext.Provider value={value}>
			{children}
		</WorkflowRunNotifierContext.Provider>
	);
};
